// Updated: 2026-08-19 09:02:00 CDT
// Purpose: Automatically load the signed-in user's screenplay library whenever the Scripts page opens.

import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { History, Pencil, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { deleteScreenplay, renameScreenplay, type ScreenplayDeletion } from "@/utils/screenplayManagement";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { toast } from "sonner";

type ScreenplayRow = Pick<
  Tables<"screenplays">,
  | "id"
  | "title"
  | "original_filename"
  | "status"
  | "parsed_characters"
  | "parse_warnings"
  | "character_assignments"
  | "created_at"
  | "updated_at"
>;

type JobRow = Pick<
  Tables<"generation_jobs">,
  | "id"
  | "screenplay_id"
  | "status"
  | "total_segments"
  | "completed_segments"
  | "failed_segments"
  | "output_duration_seconds"
  | "error_message"
  | "created_at"
>;

export type ScreenplayHistoryItem = ScreenplayRow & {
  latestJob: JobRow | null;
};

interface ScreenplayHistoryProps {
  userId: string;
  activeScreenplayId: string | null;
  onOpen: (item: ScreenplayHistoryItem) => void | Promise<void>;
  onRenamed?: (id: string, name: string) => void;
  onDeleted?: (id: string) => void;
  onBeforeDelete?: (id: string) => Promise<boolean>;
}

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
  timeStyle: "short",
});

const statusVariant = (status: string) =>
  status === "failed" ? "destructive" as const : "secondary" as const;

export default function ScreenplayHistory({
  userId,
  activeScreenplayId,
  onOpen,
  onRenamed, onDeleted, onBeforeDelete,
}: ScreenplayHistoryProps) {
  const [items, setItems] = useState<ScreenplayHistoryItem[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [opening, setOpening] = useState(false);
  const listId = useId();
  const [isLoading, setIsLoading] = useState(true);
  const [renaming, setRenaming] = useState<ScreenplayHistoryItem | null>(null);
  const [deleting, setDeleting] = useState<ScreenplayHistoryItem | null>(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [cleanups, setCleanups] = useState<Record<string, { name: string; result: ScreenplayDeletion }>>({});
  const hasPendingCleanup = Object.values(cleanups).some(c => !c.result.cleanup_complete);
  useEffect(() => {
    if (!hasPendingCleanup) return;
    let disposed = false;
    const timer = setTimeout(async () => {
      for (const [id, cleanup] of Object.entries(cleanups)) {
        if (cleanup.result.cleanup_complete || disposed) continue;
        try {
          const result = await deleteScreenplay(id, "status");
          if (!disposed) setCleanups(previous => ({ ...previous, [id]: { ...cleanup, result } }));
        } catch { // Keep the pending message, never report storage reclaimed on failure.
          if (!disposed) setCleanups(previous => ({ ...previous }));
        }
      }
    }, 10000);
    return () => { disposed = true; clearTimeout(timer); };
  }, [cleanups, hasPendingCleanup]);

  const confirmRename = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!renaming || busy) return;
    setBusy(true); setError("");
    try {
      const title = await renameScreenplay(userId, renaming.id, name);
      setItems(previous => previous.map(item => item.id === renaming.id ? { ...item, title, original_filename: title } : item));
      onRenamed?.(renaming.id, title); setRenaming(null);
      toast.success("Screenplay renamed.");
    } catch (e) { setError(e instanceof Error ? e.message : "Rename failed. Please retry."); }
    finally { setBusy(false); }
  };
  const confirmDelete = async () => {
    if (!deleting || busy) return;
    setBusy(true); setError("");
    try {
      if (onBeforeDelete && !(await onBeforeDelete(deleting.id))) throw new Error("A save is still pending. Resolve it before deleting.");
      const result = await deleteScreenplay(deleting.id);
      setCleanups(previous => ({ ...previous, [deleting.id]: { name: deleting.title, result } }));
      setItems(previous => previous.filter(item => item.id !== deleting.id));
      onDeleted?.(deleting.id); setDeleting(null);
      toast.success(result.cleanup_complete ? "Screenplay deleted. Audio cleanup complete." : "Screenplay deleted. Server audio cleanup is in progress.");
    } catch (e) { setError(e instanceof Error ? e.message : "Deletion failed. Please retry."); }
    finally { setBusy(false); }
  };

  const loadHistory = useCallback(async () => {
    setIsLoading(true);

    try {
      const { data: screenplays, error: screenplayError } = await supabase
        .from("screenplays")
        .select(
          "id,title,original_filename,status,parsed_characters,parse_warnings,character_assignments,created_at,updated_at",
        )
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(1000);

      if (screenplayError) {
        throw screenplayError;
      }

      const screenplayRows = screenplays ?? [];
      const screenplayIds = screenplayRows.map((screenplay) => screenplay.id);
      let jobs: JobRow[] = [];

      if (screenplayIds.length > 0) {
        const { data: jobRows, error: jobError } = await supabase
          .from("generation_jobs")
          .select(
            "id,screenplay_id,status,total_segments,completed_segments,failed_segments,output_duration_seconds,error_message,created_at",
          )
          .eq("user_id", userId)
          .in("screenplay_id", screenplayIds)
          .order("created_at", { ascending: false })
          .limit(100);

        if (jobError) {
          throw jobError;
        }

        jobs = jobRows ?? [];
      }

      const latestJobs = new Map<string, JobRow>();
      for (const job of jobs) {
        if (!latestJobs.has(job.screenplay_id)) {
          latestJobs.set(job.screenplay_id, job);
        }
      }

      setItems(
        screenplayRows.map((screenplay) => ({
          ...screenplay,
          latestJob: latestJobs.get(screenplay.id) ?? null,
        })),
      );

      console.debug("[ScreenplayHistory] History loaded:", {
        userId,
        screenplayCount: screenplayRows.length,
        jobCount: jobs.length,
      });
    } catch (error) {
      console.error("[ScreenplayHistory] History load failed:", {
        userId,
        error,
      });
      toast.error(
        error instanceof Error
          ? `Unable to load screenplay history: ${error.message}`
          : "Unable to load screenplay history.",
      );
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  const hasHistory = items.length > 0;
  // Selecting a library entry only displays its properties. Opening it remains
  // an explicit action, so browsing cannot replace a draft or start conversion.
  const selected = items.find(item => item.id === selectedId)
    ?? items.find(item => item.id === activeScreenplayId)
    ?? items[0];
  const actionsDisabled = busy || opening || isLoading;
  const openSelected = async () => {
    if (!selected || actionsDisabled) return;
    setOpening(true);
    try { await onOpen(selected); }
    catch (e) { toast.error(e instanceof Error ? e.message : "Unable to open screenplay. Please retry."); }
    finally { setOpening(false); }
  };
  const historyDescription = useMemo(
    () => hasHistory
      ? "Select a screenplay to view its properties, then choose an action."
      : "Uploaded screenplays will appear here.",
    [hasHistory],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <History className="h-5 w-5" />
          Your Screenplays
        </CardTitle>
        <CardDescription>{historyDescription}</CardDescription>
      </CardHeader>
      <CardContent>
        {Object.entries(cleanups).map(([id, cleanup]) => <p key={id} role="status" className="mb-3 text-sm text-muted-foreground">
          {cleanup.name}: {cleanup.result.cleanup_complete
            ? "Deleted. Audio cleanup complete; files still used by other screenplays were kept."
            : `Deleted. Reclaiming server storage (${cleanup.result.pending_files} files pending). Cleanup continues after you leave; in-progress uploads must finish or expire first.`}
        </p>)}
        {isLoading && !hasHistory ? (
          <p className="text-sm text-muted-foreground" role="status">
            Loading screenplay history…
          </p>
        ) : !hasHistory ? (
          <p className="text-sm text-muted-foreground">
            No screenplays have been uploaded yet.
          </p>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2" role="group" aria-label="Selected screenplay actions">
              <Button type="button" size="sm" disabled={!selected || actionsDisabled} onClick={() => void openSelected()}>
                {opening ? "Opening…" : "Open"}
              </Button>
              <Button type="button" variant="outline" size="sm" disabled={!selected || actionsDisabled}
                aria-label={selected ? `Rename ${selected.title}` : "Rename screenplay"}
                onClick={() => { setRenaming(selected); setName(selected.original_filename); setError(""); }}>
                <Pencil className="mr-2 h-4 w-4" />Rename
              </Button>
              <Button type="button" variant="outline" size="sm" disabled={!selected || actionsDisabled}
                aria-label={selected ? `Delete ${selected.title}` : "Delete screenplay"}
                onClick={() => { setDeleting(selected); setError(""); }}>
                <Trash2 className="mr-2 h-4 w-4" />Delete
              </Button>
            </div>
            <div className="grid gap-5 md:grid-cols-2">
              <div className="min-w-0 space-y-2">
                <label htmlFor={listId} className="block text-sm font-medium">Screenplays ({items.length})</label>
                <select id={listId} size={10} value={selected?.id ?? ""} disabled={actionsDisabled}
                  aria-describedby={`${listId}-help`} aria-controls={`${listId}-properties`}
                  onChange={event => setSelectedId(event.target.value)}
                  className="h-80 w-full rounded-md border border-input bg-background p-1 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50">
                  {items.map(item => <option key={item.id} value={item.id} className="rounded px-3 py-2">
                    {item.title}{item.id === activeScreenplayId ? " (Current)" : ""}
                  </option>)}
                </select>
                <p id={`${listId}-help`} className="text-xs text-muted-foreground">Use the arrow keys or click a name. Choose Open to load it in the editor.</p>
              </div>
              {selected && <section id={`${listId}-properties`} aria-label="Screenplay properties"
                className="min-w-0 rounded-lg border border-border bg-background/40 p-4">
                <h3 className="text-sm font-medium">Properties</h3>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <p className="break-words font-medium [overflow-wrap:anywhere]">{selected.title}</p>
                  {selected.id === activeScreenplayId && <Badge variant="outline">Current</Badge>}
                </div>
                <dl className="mt-4 space-y-3 text-sm [&_dt]:text-muted-foreground [&_dd]:mt-1 [&_dd]:break-words [&_dd]:[overflow-wrap:anywhere]">
                  <div><dt>Filename</dt><dd>{selected.original_filename}</dd></div>
                  <div><dt>Screenplay status</dt><dd><Badge variant={statusVariant(selected.status)}>{selected.status}</Badge></dd></div>
                  <div><dt>Uploaded</dt><dd>{dateFormatter.format(new Date(selected.created_at))}</dd></div>
                  <div><dt>Last updated</dt><dd>{dateFormatter.format(new Date(selected.updated_at ?? selected.created_at))}</dd></div>
                  <div><dt>Latest full-generation job</dt><dd>{selected.latestJob
                    ? <><Badge variant={statusVariant(selected.latestJob.status)}>{selected.latestJob.status}</Badge>
                      <span className="ml-2">{selected.latestJob.completed_segments}/{selected.latestJob.total_segments} segments
                        {selected.latestJob.failed_segments > 0 ? ` · ${selected.latestJob.failed_segments} failed` : ""}</span></>
                    : "No full-generation job recorded"}</dd></div>
                </dl>
                <p className="mt-4 text-xs text-muted-foreground">Current editor audio readiness is shown in the editor, separately from full-generation jobs.</p>
              </section>}
            </div>
          </div>
        )}
      </CardContent>
      <Dialog open={Boolean(renaming)} onOpenChange={open => { if (!open && !busy) setRenaming(null); }}>
        <DialogContent>
          <form onSubmit={event => void confirmRename(event)}>
            <DialogHeader><DialogTitle>Rename screenplay</DialogTitle>
              <DialogDescription>Change the name shown in your library and editor. Your text and audio stay unchanged.</DialogDescription></DialogHeader>
            <label htmlFor="screenplay-name" className="mt-4 block text-sm">Screenplay name</label>
            <Input id="screenplay-name" value={name} onChange={event => setName(event.target.value)} maxLength={200} disabled={busy} autoFocus />
            {error && <p role="alert" className="mt-2 text-sm text-destructive">{error}</p>}
            <DialogFooter className="mt-4"><Button type="button" variant="outline" disabled={busy} onClick={() => setRenaming(null)}>Cancel</Button>
              <Button type="submit" disabled={busy || !name.trim()}>{busy ? "Saving…" : "Save name"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <AlertDialog open={Boolean(deleting)} onOpenChange={open => { if (!open && !busy) setDeleting(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete “{deleting?.title}”?</AlertDialogTitle>
            <AlertDialogDescription>This permanently deletes the screenplay, its uploaded file and its associated generated audio from the server, freeing that storage. This cannot be undone. Audio shared with another screenplay is kept. In-progress uploads may delay final storage cleanup.</AlertDialogDescription></AlertDialogHeader>
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
          <AlertDialogFooter><AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <Button variant="destructive" disabled={busy} onClick={() => void confirmDelete()}>{busy ? "Deleting…" : "Delete screenplay and audio"}</Button></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
